import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { shortDateFormat } from '@utils/dateWithTz';
import { InvoiceOutDTO, InvoiceLineInDTO, PermissionTypesEnum } from '@api/ringil-api.v2';
import useForm from '@hooks/useForm/useForm';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency';
import { InputNumber } from '@components/framework/input/InputNumber';
import { LovWithoutIdDTO } from '@api/ringil-api.v2';
import { stringIsoFromDateInTz } from '@utils/dateWithTz';
import { Field } from '@components/framework/formfield/Field.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import AddonsSvg from '@app/icons/AddonsSvg.tsx';
import { useTranslation } from 'react-i18next';
import { buildInvoiceUpdateRequest, FormValues, invoiceToFormValues, newInvoiceLine } from '@components/ringil3/Features/Attachments/editInvoiceMapping';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  attachmentId: string;
  onUpdated?: () => void;
}

// Tolerance for rounding differences in tax calculations (cents)
const TAX_ROUNDING_TOLERANCE = 0.01;

// Reusable validation helper functions
const validateDecimalPlaces = (maxPlaces: number) => (value: any) => {
  if (value == null) return true;
  const decimalPlaces = (value.toString().split('.')[1] || '').length;
  return decimalPlaces <= maxPlaces;
};

const validateTotalDigits = (maxDigits: number) => (value: any) => {
  if (value == null) return true;
  const totalDigits = value.toString().replace('.', '').replace('-', '').length;
  return totalDigits <= maxDigits;
};

const createTaxSumValidator = (exclusiveField: string, taxField: string, inclusiveField: string) =>
  function(this: any, value: number | null | undefined) {
    const parent = (this as any).parent;
    const exclusive = parent[exclusiveField];
    const tax = parent[taxField];
    const inclusive = parent[inclusiveField];

    if (value != null && exclusive != null && tax != null && inclusive != null) {
      const calculated = exclusive + tax;
      const diff = Math.abs(calculated - inclusive);
      return diff < TAX_ROUNDING_TOLERANCE;
    }
    return true;
  };

const createTaxRateValidator = (exclusiveField: string, taxField: string, rateField: string) =>
  function(this: any, value: number | null | undefined) {
    const parent = (this as any).parent;
    const exclusive = parent[exclusiveField];
    const rate = parent[rateField];

    if (value != null && rate != null && exclusive != null && exclusive > 0) {
      const calculatedTax = exclusive * rate;
      const diff = Math.abs(calculatedTax - value);

      if (diff >= TAX_ROUNDING_TOLERANCE) {
        return false; // Validation fails, error.type will be used for translation
      }
    }
    return true;
  };

const createTaxRateConsistencyValidator = (exclusiveField: string, taxField: string) =>
  function(this: any, value: number | null | undefined) {
    const parent = (this as any).parent;
    const exclusive = parent[exclusiveField];
    const tax = parent[taxField];

    if (value != null && exclusive != null && tax != null && exclusive > 0) {
      const calculatedTax = exclusive * value;
      const diff = Math.abs(calculatedTax - tax);

      if (diff >= TAX_ROUNDING_TOLERANCE) {
        return false; // Validation fails, error.type will be used for translation
      }
    }
    return true;
  };

const createInclusiveGreaterThanExclusiveValidator = (exclusiveField: string) =>
  function(this: any, value: number | null | undefined) {
    const parent = (this as any).parent;
    const exclusive = parent[exclusiveField];

    if (value != null && exclusive != null && exclusive > 0) {
      if (value < exclusive) {
        return false; // Validation fails, error.type will be used for translation
      }
    }
    return true;
  };

const createLineTaxRateValidator = (exclusiveField: string, inclusiveField: string) =>
  function(this: any) {
    const parent = (this as any).parent;
    const exclusive = parent[exclusiveField];
    const inclusive = parent[inclusiveField];
    // Get taxRate from the root form context
    const taxRate = (this as any).options?.context?.formData?.taxRate;

    if (exclusive != null && inclusive != null && taxRate != null && exclusive > 0) {
      // Calculate what the inclusive amount should be based on exclusive + tax
      const calculatedTax = exclusive * taxRate;
      const calculatedInclusive = exclusive + calculatedTax;
      const diff = Math.abs(calculatedInclusive - inclusive);

      if (diff >= TAX_ROUNDING_TOLERANCE) {
        return false; // Validation fails, error.type will be used for translation
      }
    }
    return true;
  };

// Factory function to create validation schema
const createValidationSchema = () => yup.object().shape({
  issueDate: yup.string().required('invalidValue'),
  taxPointDate: yup.string().required('invalidValue'),
  paymentDueDate: yup.string().nullable(),
  supplierName: yup.string().required('invalidValue').min(3, 'invalidValue').max(255, 'invalidValue'),
  supplierCRN: yup.string().required('invalidValue').min(3, 'invalidValue').max(64, 'invalidValue'),
  supplierTaxId: yup.string().max(64, 'invalidValue').nullable(),
  customerName: yup.string().required('invalidValue').min(3, 'invalidValue').max(255, 'invalidValue'),
  customerCRN: yup.string().required('invalidValue').min(3, 'invalidValue').max(64, 'invalidValue'),
  customerTaxId: yup.string().max(64, 'invalidValue').nullable(),
  bankIBAN: yup.string().max(34, 'invalidValue').matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/, 'invalidValue').nullable(),
  bankBIC: yup.string().max(11, 'invalidValue').matches(/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'invalidValue').nullable(),
  bankCode: yup.string().max(16, 'invalidValue').nullable(),
  variableSymbol: yup.string().max(32, 'invalidValue').nullable(),
  constantSymbol: yup.string().max(32, 'invalidValue').nullable(),
  transactionCurrCode: yup.string().required('invalidValue'),
  homeCurrCode: yup.string().required('invalidValue'),
  currRate: yup.number()
    .min(0.00001)
    .max(Number.MAX_SAFE_INTEGER)
    .test('decimal-places', 'max-decimal-places', validateDecimalPlaces(5))
    .test('total-digits', 'max-total-digits', validateTotalDigits(19))
    .nullable(),
  taxExclusiveTransactionCurrAmount: yup.number()
    .test('inconsistent-amounts', 'Inconsistent Amounts',
      createTaxSumValidator('taxExclusiveTransactionCurrAmount', 'taxTransactionCurrAmount', 'taxInclusiveTransactionCurrAmount')),
  taxInclusiveTransactionCurrAmount: yup.number()
    .test('inconsistent-amounts', 'Inconsistent Amounts',
      createTaxSumValidator('taxExclusiveTransactionCurrAmount', 'taxTransactionCurrAmount', 'taxInclusiveTransactionCurrAmount')),
  taxTransactionCurrAmount: yup.number()
    .test('inconsistent-amounts', 'Inconsistent Amounts',
      createTaxSumValidator('taxExclusiveTransactionCurrAmount', 'taxTransactionCurrAmount', 'taxInclusiveTransactionCurrAmount'))
    .test('inconsistent-amounts', 'Inconsistent Amounts',
      createTaxRateValidator('taxExclusiveTransactionCurrAmount', 'taxTransactionCurrAmount', 'taxRate')),
  taxExclusiveHomeCurrAmount: yup.number()
    .test('inconsistent-amounts', 'Inconsistent Amounts',
      createTaxSumValidator('taxExclusiveHomeCurrAmount', 'taxHomeCurrAmount', 'taxInclusiveHomeCurrAmount')),
  taxInclusiveHomeCurrAmount: yup.number()
    .test('inconsistent-amounts', 'Inconsistent Amounts',
      createTaxSumValidator('taxExclusiveHomeCurrAmount', 'taxHomeCurrAmount', 'taxInclusiveHomeCurrAmount')),
  taxHomeCurrAmount: yup.number()
    .test('inconsistent-amounts', 'Inconsistent Amounts',
      createTaxSumValidator('taxExclusiveHomeCurrAmount', 'taxHomeCurrAmount', 'taxInclusiveHomeCurrAmount'))
    .test('inconsistent-amounts', 'Inconsistent Amounts',
      createTaxRateValidator('taxExclusiveHomeCurrAmount', 'taxHomeCurrAmount', 'taxRate')),
  taxRate: yup.number()
    .min(0)
    .max(100)
    .test('decimal-places', 'max-decimal-places', validateDecimalPlaces(2))
    .test('total-digits', 'max-total-digits', validateTotalDigits(19))
    .test('inconsistent-amounts', 'Inconsistent Amounts',
      createTaxRateConsistencyValidator('taxExclusiveTransactionCurrAmount', 'taxTransactionCurrAmount'))
    .nullable(),
  invoiceLines: yup.array().of(
    yup.object().shape({
      invoiceLineId: yup.string().max(64).required('invalidValue'),
      expeditionId: yup.string().max(64).nullable(),
      lineTransactionCurrAmount: yup.number()
        .min(0)
        .max(Number.MAX_SAFE_INTEGER)
        .test('decimal-places', 'max-decimal-places', validateDecimalPlaces(2))
        .test('total-digits', 'max-total-digits', validateTotalDigits(19)),
      lineTaxInclusiveTransactionCurrAmount: yup.number()
        .min(0)
        .max(Number.MAX_SAFE_INTEGER)
        .test('decimal-places', 'max-decimal-places', validateDecimalPlaces(2))
        .test('total-digits', 'max-total-digits', validateTotalDigits(19))
        .test('inconsistent-amounts', 'Inconsistent Amounts',
          createInclusiveGreaterThanExclusiveValidator('lineTransactionCurrAmount'))
        .test('inconsistent-amounts', 'Inconsistent Amounts',
          createLineTaxRateValidator('lineTransactionCurrAmount', 'lineTaxInclusiveTransactionCurrAmount')),
      lineHomeCurrAmount: yup.number()
        .min(0)
        .max(Number.MAX_SAFE_INTEGER)
        .test('decimal-places', 'max-decimal-places', validateDecimalPlaces(2))
        .test('total-digits', 'max-total-digits', validateTotalDigits(19)),
      lineTaxInclusiveHomeCurrAmount: yup.number()
        .min(0)
        .max(Number.MAX_SAFE_INTEGER)
        .test('decimal-places', 'max-decimal-places', validateDecimalPlaces(2))
        .test('total-digits', 'max-total-digits', validateTotalDigits(19))
        .test('inconsistent-amounts', 'Inconsistent Amounts',
          createInclusiveGreaterThanExclusiveValidator('lineHomeCurrAmount'))
        .test('inconsistent-amounts', 'Inconsistent Amounts',
          createLineTaxRateValidator('lineHomeCurrAmount', 'lineTaxInclusiveHomeCurrAmount')),
      currency: yup.string().length(3).required('invalidValue'),
    })
  ).nullable(),
});

const EditInvoiceSidebar: React.FC<ComponentProps> = ({ visible, onHide, attachmentId, onUpdated }) => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { getInvoiceByAttachmentId, updateInvoice, lovCurrency } = useContext(ApiContext);
  const [invoice, setInvoice] = useState<InvoiceOutDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState<LovWithoutIdDTO[]>([]);

  const canEditInvoices = useSecuredFlag('edit-invoice-sidebar', {
    kind: 'editability',
    description: 'Edit invoice sidebar form controls',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_ATTACHMENTS_INVOICES_RW],
  }).granted;

  // Create validation schema - memoize to prevent recreation on every render
  const validationSchema = React.useMemo(() => createValidationSchema(), []);

  const form = useForm<FormValues>(
    validationSchema,
    {
      invoiceLines: [],
    },
    (data) => handleSave(data),
    true,   // validateOnChange: true - validate as user types
    false,  // validateOnMount: false
    undefined,
    'none'  // errorMode: 'none' - show errors under fields, no toast popup
  );

  // Load currencies once for the entire form
  useEffect(() => {
    if (visible) {
      lovCurrency(data => {
        setCurrencies(data || []);
      });
    }
  }, [visible, lovCurrency]);

  useEffect(() => {
    if (visible && attachmentId && canEditInvoices) {
      setLoading(true);
      setInvoice(null);

      getInvoiceByAttachmentId(
        attachmentId,
        (data: InvoiceOutDTO) => {
          if (data) {
            setInvoice(data);
            form.setValues(invoiceToFormValues(data));
          }
          setLoading(false);
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, attachmentId, canEditInvoices, getInvoiceByAttachmentId]);

  useEffect(() => {
    if (!visible) {
      setLoading(false);
      setInvoice(null);
      form.setValues({
        invoiceLines: [],
      });
    }
  }, [visible]);

  const handleSave = (formData: FormValues) => {
    if (!invoice || !canEditInvoices) return;

    setLoading(true);

    const updateData = buildInvoiceUpdateRequest(invoice, formData);

    updateInvoice(
      invoice.id,
      updateData,
      () => {
        setLoading(false);
        onUpdated?.();
        onHide();
      }
    );
  };

  const addInvoiceLine = () => {
    const currentLines = form.values.invoiceLines || [];

    form.setFieldValue('invoiceLines', [...currentLines, newInvoiceLine(form.values, invoice)]);
  };

  const removeInvoiceLine = (index: number) => {
    const currentLines = [...(form.values.invoiceLines || [])];
    currentLines.splice(index, 1);
    form.setFieldValue('invoiceLines', currentLines);
  };

  const updateInvoiceLine = (index: number, field: keyof InvoiceLineInDTO, value: string | number) => {
    const currentLines = [...(form.values.invoiceLines || [])];
    currentLines[index] = { ...currentLines[index], [field]: value };
    form.setFieldValue('invoiceLines', currentLines);
  };

  if (!canEditInvoices) {
    return null;
  }

  return (
    <RingilSidebar
      visible={visible}
      onBack={onHide}
      onContinue={form.validateAndSend}
      heading={tr('EditInvoiceSidebar.title', 'Edit Invoice')}
      loading={loading}
      continueLabel={tr('EditInvoiceSidebar.saveAndSendToERP', 'Save & Send to ERP')}
    >
      {/* Opts into the legacy PrimeReact overrides in styles/overrides/legacy-fields.scss. */}
      <div className={'r-legacy-fields'}>
        {loading && (
          <div className="text-center py-8">
            <div className="mb-4">
              <i className="pi pi-spinner pi-spin text-2xl text-blue-500"></i>
            </div>
            {tr('EditInvoiceSidebar.loading', 'Loading invoice...')}
          </div>
        )}

        {!loading && invoice && (
          <>
            {/* Supplier Information */}
            <COHeadline variant={'h3'}
                        title={tr('EditInvoiceSidebar.supplierInfo', 'Supplier Information')}/>
            <div className="grid grid-cols-1 gap-4 mb-4">
              {/* First row: Supplier Name (full width) */}
              <Field
                label={tr('EditInvoiceSidebar.supplierName', 'Supplier Name')}
                required={!form.values.supplierName}
                errorMessage={findValidationMessage(form.validationErrors, 'supplierName', tr)}
              >
                <InputText
                  value={form.values.supplierName || ''}
                  onChange={(e) => form.setFieldValue('supplierName', e.target.value)}
                  placeholder={tr('EditInvoiceSidebar.supplierNamePlaceholder', 'Enter supplier name')}
                />
              </Field>

              {/* Second row: Supplier CRN and Tax ID */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label={tr('EditInvoiceSidebar.supplierCRN', 'Supplier CRN')}
                required={!form.values.supplierCRN}
                errorMessage={findValidationMessage(form.validationErrors, 'supplierCRN', tr)}
              >
                <InputText
                  value={form.values.supplierCRN || ''}
                  onChange={(e) => form.setFieldValue('supplierCRN', e.target.value)}
                  placeholder={tr('EditInvoiceSidebar.supplierCRNPlaceholder', 'Enter supplier CRN')}
                />
              </Field>
              <Field
                label={tr('EditInvoiceSidebar.supplierTaxId', 'Supplier Tax ID')}
                required={false}
                errorMessage={findValidationMessage(form.validationErrors, 'supplierTaxId', tr)}
              >
                <InputText
                  value={form.values.supplierTaxId || ''}
                  onChange={(e) => form.setFieldValue('supplierTaxId', e.target.value)}
                  placeholder={tr('EditInvoiceSidebar.supplierTaxIdPlaceholder', 'Enter supplier tax ID')}
                />
              </Field>
              </div>
            </div>

            <HorizontalLine variant="brand-light" />

            {/* Customer Information */}
            <COHeadline variant={'h3'}
                        title={tr('EditInvoiceSidebar.customerInfo', 'Customer Information')}/>
            <div className="grid grid-cols-1 gap-4 mb-4">
              <Field
                label={tr('EditInvoiceSidebar.customerName', 'Customer Name')}
                required={!form.values.customerName}
                errorMessage={findValidationMessage(form.validationErrors, 'customerName', tr)}
              >
                <InputText
                  value={form.values.customerName || ''}
                  onChange={(e) => form.setFieldValue('customerName', e.target.value)}
                  placeholder={tr('EditInvoiceSidebar.customerNamePlaceholder', 'Enter customer name')}
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label={tr('EditInvoiceSidebar.customerCRN', 'Customer CRN')}
                required={!form.values.customerCRN}
                errorMessage={findValidationMessage(form.validationErrors, 'customerCRN', tr)}
              >
                <InputText
                  value={form.values.customerCRN || ''}
                  onChange={(e) => form.setFieldValue('customerCRN', e.target.value)}
                  placeholder={tr('EditInvoiceSidebar.customerCRNPlaceholder', 'Enter customer CRN')}
                />
              </Field>
              <Field
                label={tr('EditInvoiceSidebar.customerTaxId', 'Customer Tax ID')}
                required={false}
                errorMessage={findValidationMessage(form.validationErrors, 'customerTaxId', tr)}
              >
                <InputText
                  value={form.values.customerTaxId || ''}
                  onChange={(e) => form.setFieldValue('customerTaxId', e.target.value)}
                  placeholder={tr('EditInvoiceSidebar.customerTaxIdPlaceholder', 'Enter customer tax ID')}
                />
              </Field>
              </div>
            </div>

            <HorizontalLine variant="brand-light" />

            {/* Invoice Dates */}
            <COHeadline variant={'h3'}
                        title={tr('EditInvoiceSidebar.invoiceDates', 'Invoice Dates')}/>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Field
                label={tr('EditInvoiceSidebar.issueDate', 'Issue Date')}
                required={!form.values.issueDate}
                errorMessage={findValidationMessage(form.validationErrors, 'issueDate', tr)}
              >
                <Calendar
                  locale={i18n.language}
                  selectionMode={'single'}
                  value={form.values.issueDate ? new Date(form.values.issueDate) : null}
                  onChange={e =>
                    form.setFieldValue('issueDate', e.value && e.value instanceof Date ? stringIsoFromDateInTz(e.value as Date, loggedUser?.defaultTimezoneId ?? 'Europe/Prague') : null)
                  }
                  showIcon
                  icon={<AddonsSvg width={20} height={20} />}
                  dateFormat={shortDateFormat}
                  placeholder={tr('EditInvoiceSidebar.selectDate', 'Select date')}
                />
              </Field>
              <Field
                label={tr('EditInvoiceSidebar.taxPointDate', 'Tax Point Date')}
                required={!form.values.taxPointDate}
                errorMessage={findValidationMessage(form.validationErrors, 'taxPointDate', tr)}
              >
                <Calendar
                  locale={i18n.language}
                  selectionMode={'single'}
                  value={form.values.taxPointDate ? new Date(form.values.taxPointDate) : null}
                  onChange={e =>
                    form.setFieldValue('taxPointDate', e.value && e.value instanceof Date ? stringIsoFromDateInTz(e.value as Date, loggedUser?.defaultTimezoneId ?? 'Europe/Prague') : null)
                  }
                  showIcon
                  icon={<AddonsSvg width={20} height={20} />}
                  dateFormat={shortDateFormat}
                  placeholder={tr('EditInvoiceSidebar.selectDate', 'Select date')}
                />
              </Field>
              <Field
                label={tr('EditInvoiceSidebar.paymentDueDate', 'Payment Due Date')}
                required={!form.values.paymentDueDate}
                errorMessage={findValidationMessage(form.validationErrors, 'paymentDueDate', tr)}
              >
                <Calendar
                  locale={i18n.language}
                  selectionMode={'single'}
                  value={form.values.paymentDueDate ? new Date(form.values.paymentDueDate) : null}
                  onChange={e =>
                    form.setFieldValue('paymentDueDate', e.value && e.value instanceof Date ? stringIsoFromDateInTz(e.value as Date, loggedUser?.defaultTimezoneId ?? 'Europe/Prague') : null)
                  }
                  showIcon
                  icon={<AddonsSvg width={20} height={20} />}
                  dateFormat={shortDateFormat}
                  placeholder={tr('EditInvoiceSidebar.selectDate', 'Select date')}
                />
              </Field>
            </div>

            <HorizontalLine variant="brand-light" />

            {/* Banking Information */}
            <COHeadline variant={'h3'}
                        title={tr('EditInvoiceSidebar.bankingInfo', 'Banking Information')}/>
            <div className="grid grid-cols-1 gap-4 mb-4">
              <Field
                label={tr('EditInvoiceSidebar.bankIBAN', 'Bank IBAN')}
                required={false}
                errorMessage={findValidationMessage(form.validationErrors, 'bankIBAN', tr)}
              >
                <InputText
                  value={form.values.bankIBAN || ''}
                  onChange={(e) => form.setFieldValue('bankIBAN', e.target.value)}
                  placeholder={tr('EditInvoiceSidebar.bankIBANPlaceholder', 'Enter bank IBAN')}
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={tr('EditInvoiceSidebar.bankBIC', 'Bank BIC')}
                  required={false}
                  errorMessage={findValidationMessage(form.validationErrors, 'bankBIC', tr)}
                >
                  <InputText
                    value={form.values.bankBIC || ''}
                    onChange={(e) => form.setFieldValue('bankBIC', e.target.value)}
                    placeholder={tr('EditInvoiceSidebar.bankBICPlaceholder', 'Enter bank BIC')}
                  />
                </Field>
                <Field
                  label={tr('EditInvoiceSidebar.bankCode', 'Bank Code')}
                  required={false}
                  errorMessage={findValidationMessage(form.validationErrors, 'bankCode', tr)}
                >
                  <InputText
                    value={form.values.bankCode || ''}
                    onChange={(e) => form.setFieldValue('bankCode', e.target.value)}
                    placeholder={tr('EditInvoiceSidebar.bankCodePlaceholder', 'Enter bank code')}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={tr('EditInvoiceSidebar.variableSymbol', 'Variable Symbol')}
                  required={!form.values.variableSymbol}
                  errorMessage={findValidationMessage(form.validationErrors, 'variableSymbol', tr)}
                >
                  <InputText
                    value={form.values.variableSymbol || ''}
                    onChange={(e) => form.setFieldValue('variableSymbol', e.target.value)}
                    placeholder={tr('EditInvoiceSidebar.variableSymbolPlaceholder', 'Enter variable symbol')}
                  />
                </Field>
                <Field
                  label={tr('EditInvoiceSidebar.constantSymbol', 'Constant Symbol')}
                  required={false}
                  errorMessage={findValidationMessage(form.validationErrors, 'constantSymbol', tr)}
                >
                  <InputText
                    value={form.values.constantSymbol || ''}
                    onChange={(e) => form.setFieldValue('constantSymbol', e.target.value)}
                    placeholder={tr('EditInvoiceSidebar.constantSymbolPlaceholder', 'Enter constant symbol')}
                  />
                </Field>
              </div>
            </div>

            <HorizontalLine variant="brand-light" />

            {/* Invoice Amounts */}
            <COHeadline variant={'h3'}
                        title={tr('EditInvoiceSidebar.invoiceAmounts', 'Invoice Amounts')}/>
            <div className="grid grid-cols-1 gap-4 mb-4">
              {/* Transaction Currency Amounts */}
              <COHeadline variant={'h4'}
                          title={tr('EditInvoiceSidebar.transactionCurrencyAmounts', 'Transaction Currency Amounts')}/>
              <Field
                label={tr('EditInvoiceSidebar.taxExclusiveAmount', 'Amount (Excl. Tax)')}
                required={!form.values.taxExclusiveTransactionCurrAmount || !form.values.transactionCurrCode}
                errorMessage={findValidationMessage(form.validationErrors, 'taxExclusiveTransactionCurrAmount', tr) || findValidationMessage(form.validationErrors, 'transactionCurrCode', tr)}
              >
                <InputPriceWithCurrency
                  value={form.values.taxExclusiveTransactionCurrAmount || 0}
                  currency={form.values.transactionCurrCode || ''}
                  changePrice={(price) => form.setFieldValue('taxExclusiveTransactionCurrAmount', price)}
                  changeCurrency={(currency) => form.setFieldValue('transactionCurrCode', currency)}
                  currencies={currencies}
                />
              </Field>
              <Field
                label={tr('EditInvoiceSidebar.taxAmount', 'Tax Amount')}
                required={!form.values.taxTransactionCurrAmount || !form.values.transactionCurrCode}
                errorMessage={findValidationMessage(form.validationErrors, 'taxTransactionCurrAmount', tr) || findValidationMessage(form.validationErrors, 'transactionCurrCode', tr)}
              >
                <InputPriceWithCurrency
                  value={form.values.taxTransactionCurrAmount || 0}
                  currency={form.values.transactionCurrCode || ''}
                  changePrice={(price) => form.setFieldValue('taxTransactionCurrAmount', price)}
                  changeCurrency={(currency) => form.setFieldValue('transactionCurrCode', currency)}
                  currencies={currencies}
                />
              </Field>
              <Field
                label={tr('EditInvoiceSidebar.taxInclusiveAmount', 'Amount (Inc. Tax)')}
                required={!form.values.taxInclusiveTransactionCurrAmount || !form.values.transactionCurrCode}
                errorMessage={findValidationMessage(form.validationErrors, 'taxInclusiveTransactionCurrAmount', tr) || findValidationMessage(form.validationErrors, 'transactionCurrCode', tr)}
              >
                <InputPriceWithCurrency
                  value={form.values.taxInclusiveTransactionCurrAmount || 0}
                  currency={form.values.transactionCurrCode || ''}
                  changePrice={(price) => form.setFieldValue('taxInclusiveTransactionCurrAmount', price)}
                  changeCurrency={(currency) => form.setFieldValue('transactionCurrCode', currency)}
                  currencies={currencies}
                />
              </Field>

              {/* Home Currency Amounts */}
              <COHeadline variant={'h4'}
                          title={tr('EditInvoiceSidebar.homeCurrencyAmounts', 'Home Currency Amounts')}/>
              <Field
                label={tr('EditInvoiceSidebar.taxExclusiveAmountHome', 'Amount (Excl. Tax)')}
                required={!form.values.taxExclusiveHomeCurrAmount || !form.values.homeCurrCode}
                errorMessage={findValidationMessage(form.validationErrors, 'taxExclusiveHomeCurrAmount', tr) || findValidationMessage(form.validationErrors, 'homeCurrCode', tr)}
              >
                <InputPriceWithCurrency
                  value={form.values.taxExclusiveHomeCurrAmount || 0}
                  currency={form.values.homeCurrCode || ''}
                  changePrice={(price) => form.setFieldValue('taxExclusiveHomeCurrAmount', price)}
                  changeCurrency={(currency) => form.setFieldValue('homeCurrCode', currency)}
                  disabledCurrency={true}
                  currencies={currencies}
                />
              </Field>
              <Field
                label={tr('EditInvoiceSidebar.taxAmountHome', 'Tax Amount')}
                required={!form.values.taxHomeCurrAmount || !form.values.homeCurrCode}
                errorMessage={findValidationMessage(form.validationErrors, 'taxHomeCurrAmount', tr) || findValidationMessage(form.validationErrors, 'homeCurrCode', tr)}
              >
                <InputPriceWithCurrency
                  value={form.values.taxHomeCurrAmount || 0}
                  currency={form.values.homeCurrCode || ''}
                  changePrice={(price) => form.setFieldValue('taxHomeCurrAmount', price)}
                  changeCurrency={(currency) => form.setFieldValue('homeCurrCode', currency)}
                  disabledCurrency={true}
                  currencies={currencies}
                />
              </Field>
              <Field
                label={tr('EditInvoiceSidebar.taxInclusiveAmountHome', 'Amount (Inc. Tax)')}
                required={!form.values.taxInclusiveHomeCurrAmount || !form.values.homeCurrCode}
                errorMessage={findValidationMessage(form.validationErrors, 'taxInclusiveHomeCurrAmount', tr) || findValidationMessage(form.validationErrors, 'homeCurrCode', tr)}
              >
                <InputPriceWithCurrency
                  value={form.values.taxInclusiveHomeCurrAmount || 0}
                  currency={form.values.homeCurrCode || ''}
                  changePrice={(price) => form.setFieldValue('taxInclusiveHomeCurrAmount', price)}
                  changeCurrency={(currency) => form.setFieldValue('homeCurrCode', currency)}
                  disabledCurrency={true}
                  currencies={currencies}
                />
              </Field>

              {/* Exchange Rate and Tax Rate */}
              <COHeadline variant={'h4'}
                          title={tr('EditInvoiceSidebar.ratesInfo', 'Rates Information')}/>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={tr('EditInvoiceSidebar.currencyRate', 'Exchange Rate')}
                  required={!form.values.currRate}
                  errorMessage={findValidationMessage(form.validationErrors, 'currRate', tr)}
                >
                  <InputNumber
                    value={form.values.currRate || null}
                    onChange={(e) => form.setFieldValue('currRate', e.value)}
                    placeholder={tr('EditInvoiceSidebar.currencyRatePlaceholder', '1.0')}
                    minFractionDigits={0}
                    maxFractionDigits={5}
                    min={0.00001}
                    mode="decimal"
                    locale="en"
                    inputStyle={{ textAlign: 'right' }}
                  />
                </Field>
                <Field
                  label={tr('EditInvoiceSidebar.taxRate', 'Tax Rate (%)')}
                  required={form.values.taxRate == null}
                  errorMessage={findValidationMessage(form.validationErrors, 'taxRate', tr)}
                >
                  <InputNumber
                    value={form.values.taxRate != null ? form.values.taxRate * 100 : null}
                    onChange={(e) => form.setFieldValue('taxRate', e.value != null ? e.value / 100 : null)}
                    placeholder={tr('EditInvoiceSidebar.taxRatePlaceholder', '21.0')}
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    min={0}
                    max={100}
                    mode="decimal"
                    locale="en"
                    suffix=" %"
                    inputStyle={{ textAlign: 'right' }}
                  />
                </Field>
              </div>
            </div>

            <HorizontalLine variant="brand-light" />


            {/* Invoice Lines */}
            <COHeadline variant={'h3'}
                        title={tr('EditInvoiceSidebar.invoiceLines', 'Invoice Lines')}>
            </COHeadline>
            {(!form.values.invoiceLines || form.values.invoiceLines.length === 0) && (
              <div className="p-4 bg-blue-50 rounded border border-blue-200 mb-4">
                <p className="text-blue-700 text-sm">
                  {tr('EditInvoiceSidebar.noInvoiceLines', 'No invoice lines found. Add lines to specify individual amounts and expedition references.')}
                </p>
              </div>
            )}

            {(form.values.invoiceLines || []).map((line: InvoiceLineInDTO, index: number) => (
              <div key={index} className="grid grid-cols-1 gap-3 mb-4 p-4 bg-gray-50 rounded border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={tr('EditInvoiceSidebar.invoiceLineId', 'Line ID')} required={!line.invoiceLineId}>
                    <InputText
                      value={line.invoiceLineId || ''}
                      placeholder={tr('EditInvoiceSidebar.invoiceLineIdPlaceholder', 'Enter line ID')}
                      readOnly={true}
                    />
                  </Field>
                  <Field label={tr('EditInvoiceSidebar.expeditionId', 'Expedition ID')} required={false}>
                    <InputText
                      value={line.expeditionId || ''}
                      placeholder={tr('EditInvoiceSidebar.expeditionIdPlaceholder', 'Expedition ID')}
                      readOnly={true}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label={tr('EditInvoiceSidebar.lineAmount', 'Amount (Excl. Tax)')}
                    required={!line.lineTransactionCurrAmount || !line.currency}
                    errorMessage={findValidationMessage(form.validationErrors, `invoiceLines[${index}].lineTransactionCurrAmount`, tr) || findValidationMessage(form.validationErrors, `invoiceLines[${index}].currency`, tr)}
                  >
                    <InputPriceWithCurrency
                      value={line.lineTransactionCurrAmount || 0}
                      currency={line.currency || ''}
                      changePrice={(price) => updateInvoiceLine(index, 'lineTransactionCurrAmount', price)}
                      changeCurrency={(currency) => updateInvoiceLine(index, 'currency', currency)}
                      currencies={currencies}
                    />
                  </Field>

                  <Field
                    label={tr('EditInvoiceSidebar.lineAmountIncTax', 'Amount (Inc. Tax)')}
                    required={!line.lineTaxInclusiveTransactionCurrAmount || !line.currency}
                    errorMessage={findValidationMessage(form.validationErrors, `invoiceLines[${index}].lineTaxInclusiveTransactionCurrAmount`, tr) || findValidationMessage(form.validationErrors, `invoiceLines[${index}].currency`, tr)}
                  >
                    <InputPriceWithCurrency
                      value={line.lineTaxInclusiveTransactionCurrAmount || 0}
                      currency={line.currency || ''}
                      changePrice={(price) => updateInvoiceLine(index, 'lineTaxInclusiveTransactionCurrAmount', price)}
                      changeCurrency={(currency) => updateInvoiceLine(index, 'currency', currency)}
                      disabledCurrency={true}
                      currencies={currencies}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label={tr('EditInvoiceSidebar.lineHomeCurrAmount', 'Home Currency Amount (Excl. Tax)')}
                    required={!line.lineHomeCurrAmount}
                    errorMessage={findValidationMessage(form.validationErrors, `invoiceLines[${index}].lineHomeCurrAmount`, tr)}
                  >
                    <InputPriceWithCurrency
                      value={line.lineHomeCurrAmount || 0}
                      currency={form.values.homeCurrCode || ''}
                      changePrice={(price) => updateInvoiceLine(index, 'lineHomeCurrAmount', price)}
                      changeCurrency={() => {}}
                      disabledCurrency={true}
                      currencies={currencies}
                    />
                  </Field>

                  <Field
                    label={tr('EditInvoiceSidebar.lineTaxInclusiveHomeCurrAmount', 'Home Currency Amount (Inc. Tax)')}
                    required={!line.lineTaxInclusiveHomeCurrAmount}
                    errorMessage={findValidationMessage(form.validationErrors, `invoiceLines[${index}].lineTaxInclusiveHomeCurrAmount`, tr)}
                  >
                    <InputPriceWithCurrency
                      value={line.lineTaxInclusiveHomeCurrAmount || 0}
                      currency={form.values.homeCurrCode || ''}
                      changePrice={(price) => updateInvoiceLine(index, 'lineTaxInclusiveHomeCurrAmount', price)}
                      changeCurrency={() => {}}
                      disabledCurrency={true}
                      currencies={currencies}
                    />
                  </Field>
                </div>

                <div className="flex justify-end">
                  <RingilButton
                    icon={<i className="pi pi-trash"></i>}
                    mode="text-only"
                    size="small"
                    onClick={() => removeInvoiceLine(index)}
                    tooltip={tr('EditInvoiceSidebar.removeLine', 'Remove line')}
                  />
                </div>
              </div>
            ))}
            <RingilButton
              label={tr('EditInvoiceSidebar.addLine', 'Add Invoice Line')}
              icon={<i className="pi pi-plus"></i>}
              mode="outlined"
              size="small"
              onClick={addInvoiceLine}
            />

          </>
        )}
      </div>
    </RingilSidebar>
  );
};

export default EditInvoiceSidebar;

